import { belongsToOkrTeam } from './okrTeamScope';
import { getCalculationTypeLabel } from './okrCalculationLabel';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { TabPanel, TabView } from 'primereact/tabview';
import { Tag } from 'primereact/tag';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { endPoints } from '../../Config';
import { handleGetRequest } from '../../service/GetTemplate';
import OkrCycleDialog from './OkrCycleDialog';
import OkrCycleCopyDialog from './OkrCycleCopyDialog';
import { CALENDAR_MODE, defaultCycle } from './okrCycleHelpers';
import OkrExecutiveDashboard from './OkrExecutiveDashboard';
import OkrKeyResultDialog from './OkrKeyResultDialog';
import OkrMyOkrs from './OkrMyOkrs';
import OkrObjectiveDialog from './OkrObjectiveDialog';
import OkrTeamDialog from './OkrTeamDialog';
import OkrWeeklyEntryDialog from './OkrWeeklyEntryDialog';
import OkrWeeklyUpdates from './OkrWeeklyUpdates';
import OkrKpiHistory from './OkrKpiHistory';
import './okr.css';
import {
    approveOkrWeeklyEntry,
    closeOkrCycle,
    previewOkrCycleCopy,
    copyOkrCycle,
    createOrUpdateOkrWeeklyEntry,
    createOkrCycle,
    createOkrKeyResult,
    createOkrObjective,
    createOkrTeam,
    deleteOkrKeyResult,
    deleteOkrObjective,
    deleteOkrTeam,
    getOkrCycle,
    getOkrCycles,
    getOkrDashboard,
    getOkrKeyResults,
    getOkrObjectives,
    getOkrScoreboard,
    getOkrTeams,
    getOkrWeeklyEntries,
    lockOkrWeek,
    rejectOkrWeeklyEntry,
    syncOkrKeyResultWeek,
    updateOkrCycle,
    updateOkrKeyResult,
    updateOkrObjective,
    updateOkrTeam,
    updateOkrWeeklyEntry
} from './okrApi';

const getCurrentUser = () => {
    try {
        const storedUser = JSON.parse(localStorage.getItem('userDetails') || '{}');
        const storedAdmin = JSON.parse(localStorage.getItem('adminUser') || '{}');
        return storedUser?.data?.adminUser || storedAdmin?.data?.adminUser || storedUser?.data?.user || storedAdmin?.data?.user || {};
    } catch (error) {
        return {};
    }
};

const hasFullAccessOkr = (user = {}) => ['admin', 'superAdmin'].includes(user?.role?.uid);

const formatNumber = (value) => {
    const number = Number(value || 0);
    return Number.isFinite(number) ? number.toLocaleString('es-PA', { maximumFractionDigits: 2 }) : '0';
};

const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('es-PA', { timeZone: 'America/Panama', day: '2-digit', month: 'short', year: 'numeric' });
};

const statusSeverity = (status) => {
    if (status === 'green' || status === 'active') return 'success';
    if (status === 'yellow' || status === 'draft') return 'warning';
    if (status === 'red' || status === 'closed') return 'danger';
    return 'info';
};

const getStatusLabel = (status) => {
    if (status === 'green') return 'En meta';
    if (status === 'yellow') return 'En observación';
    if (status === 'red') return 'En riesgo';
    if (status === 'active') return 'Activo';
    if (status === 'draft') return 'Borrador';
    if (status === 'closed') return 'Cerrado';
    return status || '-';
};

const getUserId = (user) => user?._id || user?.id || user || '';

const OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;
const isObjectIdString = (value) => typeof value === 'string' && OBJECT_ID_PATTERN.test(value);

const getUserName = (user) => {
    if (!user) return '-';
    if (typeof user === 'string') return isObjectIdString(user) ? 'Usuario sin nombre' : user;
    if (user.name || user.fullName) return user.name || user.fullName;
    const name = `${user.firstName || user.firstname || ''} ${user.lastName || user.lastname || ''}`.trim();
    return name || user.displayName || user.userName || user.username || user.email || '-';
};

const getPointOwnerId = (keyResult) => {
    return getUserId(keyResult?.ownerId || keyResult?.pointOwnerUserId || keyResult?.updateResponsibleUserId);
};

const getPointOwnerName = (keyResult, findUserById) => {
    const owner = keyResult?.ownerId || keyResult?.pointOwnerUserId || keyResult?.updateResponsibleUserId;
    if (!owner) return 'Sin responsable';
    return getUserName(findUserById(owner) || owner);
};

const getScoringMethodLabel = (method) => {
    const labels = {
        higher_is_better: 'Mayor es mejor',
        lower_is_better: 'Menor es mejor',
        yes_no: 'Sí / No',
        percentage: 'Porcentaje',
        milestone: 'Hito / avance',
        manual_override: 'Manual / override',
        max_allowed: 'Máximo permitido'
    };
    return labels[method] || method || '-';
};


const EmptyState = ({ title, description, action }) => (
    <div className="surface-card border-round p-5 text-center shadow-1">
        <i className="pi pi-flag text-4xl text-primary mb-3" />
        <h3 className="mt-0 mb-2">{title}</h3>
        {description ? <p className="text-color-secondary m-0">{description}</p> : null}
        {action || null}
    </div>
);

const getMutationData = (response) => response?.data?.data || response?.data?.result || response?.data?.doc || response?.data || response;
const hasMutationSucceeded = (response, data) => {
    const status = Number(response?.status || response?.statusCode || 0);
    if (status >= 200 && status < 300) return true;
    return Boolean(data?._id);
};
const getMutationMessage = (response) => response?.data?.messages || response?.data?.message || response?.data?.error || response?.message || '';

const csvEscape = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
};

const downloadCsv = (filename, columns, rows) => {
    const header = columns.map((column) => csvEscape(column.header)).join(',');
    const body = rows.map((row) => columns.map((column) => csvEscape(column.value(row))).join(',')).join('\n');
    const blob = new Blob([`\ufeff${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const safeFilenamePart = (value) =>
    String(value || 'ciclo')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '') || 'ciclo';

const OKRModule = () => {
    const dispatch = useDispatch();
    const currentUser = useMemo(() => getCurrentUser(), []);
    const currentUserId = getUserId(currentUser);
    const canCreateCycle = hasFullAccessOkr(currentUser);
    const canFilterDashboardByTeam = ['admin', 'superAdmin'].includes(currentUser?.role?.uid);
    const canAccessOkr = true;
    const canManageWeeklyEntries = canCreateCycle;

    const [cycles, setCycles] = useState([]);
    const [selectedCycleId, setSelectedCycleId] = useState('');
    const selectionRef = useRef('');
    selectionRef.current = selectedCycleId;
    const [copyDialogVisible, setCopyDialogVisible] = useState(false);
    const [copyBusy, setCopyBusy] = useState(false);
    const [copyPreview, setCopyPreview] = useState(null);
    const [copyError, setCopyError] = useState('');
    const [dashboard, setDashboard] = useState(null);
    const [scoreboard, setScoreboard] = useState([]);
    const [isLoadingCycles, setIsLoadingCycles] = useState(true);
    const [isLoadingSummary, setIsLoadingSummary] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [cycleDialogVisible, setCycleDialogVisible] = useState(false);
    const [cycleDialogMode, setCycleDialogMode] = useState('create');
    const [isSavingCycle, setIsSavingCycle] = useState(false);
    const [teams, setTeams] = useState([]);
    const [users, setUsers] = useState([]);
    const [isLoadingTeams, setIsLoadingTeams] = useState(false);
    const [teamDialogVisible, setTeamDialogVisible] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [isSavingTeam, setIsSavingTeam] = useState(false);
    const [objectives, setObjectives] = useState([]);
    const [isLoadingObjectives, setIsLoadingObjectives] = useState(false);
    const [objectiveDialogVisible, setObjectiveDialogVisible] = useState(false);
    const [selectedObjective, setSelectedObjective] = useState(null);
    const [objectiveTeamId, setObjectiveTeamId] = useState('');
    const [isSavingObjective, setIsSavingObjective] = useState(false);
    const [keyResults, setKeyResults] = useState([]);
    const [isLoadingKeyResults, setIsLoadingKeyResults] = useState(false);
    const [keyResultDialogVisible, setKeyResultDialogVisible] = useState(false);
    const [selectedKeyResult, setSelectedKeyResult] = useState(null);
    const [keyResultContext, setKeyResultContext] = useState({ team: null, objective: null });
    const [isSavingKeyResult, setIsSavingKeyResult] = useState(false);
    const [weeklyEntries, setWeeklyEntries] = useState([]);
    const [isLoadingWeeklyEntries, setIsLoadingWeeklyEntries] = useState(false);
    const [reportWeeklyEntries, setReportWeeklyEntries] = useState([]);
    const [isLoadingReportWeeklyEntries, setIsLoadingReportWeeklyEntries] = useState(false);
    const [selectedWeekNumber, setSelectedWeekNumber] = useState(null);
    const weekRef = useRef(null);
    weekRef.current = selectedWeekNumber;
    const [weeklyEntryDialogVisible, setWeeklyEntryDialogVisible] = useState(false);
    const [weeklyEntryContext, setWeeklyEntryContext] = useState({ keyResult: null, team: null, objective: null, entry: null, locked: false, weekNumber: null });
    const [isSavingWeeklyEntry, setIsSavingWeeklyEntry] = useState(false);
    const [reviewingEntryId, setReviewingEntryId] = useState('');
    const [isSavingCycleControl, setIsSavingCycleControl] = useState(false);
    const [lockingWeekNumber, setLockingWeekNumber] = useState(null);

    const isTeamManager = useCallback(
        (team) => Boolean(team?._id && getUserId(team.managerId) === currentUserId),
        [currentUserId]
    );

    const canManageTeamConfiguration = useCallback(
        (team) => canCreateCycle || isTeamManager(team),
        [canCreateCycle, isTeamManager]
    );

    const isCurrentUserManagerForAnyTeam = useMemo(
        () => teams.some((team) => isTeamManager(team)),
        [isTeamManager, teams]
    );
    const canViewWeeklyReview = canCreateCycle || isCurrentUserManagerForAnyTeam;

    const selectedCycle = useMemo(
        () => cycles.find((cycle) => cycle._id === selectedCycleId) || (dashboard?.cycle?._id === selectedCycleId ? dashboard.cycle : null),
        [cycles, dashboard, selectedCycleId]
    );

    const currentWeek = (dashboard?.cycle?._id === selectedCycleId ? dashboard.currentWeek : null) ||
        selectedCycle?.weeks?.find(week => new Date(week.startDate) <= new Date() && new Date(week.endDate) >= new Date()) || null;
    const selectedWeek = useMemo(
        () => (Array.isArray(selectedCycle?.weeks) ? selectedCycle.weeks.find((week) => Number(week.weekNumber) === Number(selectedWeekNumber)) : null),
        [selectedCycle, selectedWeekNumber]
    );

    const cycleOptions = useMemo(
        () =>
            cycles.map((cycle) => ({
                label: `${cycle.name}${cycle.status === 'active' ? ' - Activo' : ''}`,
                value: cycle._id
            })),
        [cycles]
    );

    const objectivesByTeamId = useMemo(
        () =>
            objectives.reduce((grouped, objective) => {
                const teamId = getUserId(objective?.teamId);
                if (!teamId) return grouped;
                if (!grouped[teamId]) grouped[teamId] = [];
                grouped[teamId].push(objective);
                return grouped;
            }, {}),
        [objectives]
    );

    const keyResultsByObjectiveId = useMemo(
        () =>
            keyResults.reduce((grouped, keyResult) => {
                const objectiveId = getUserId(keyResult?.objectiveId);
                if (!objectiveId) return grouped;
                if (!grouped[objectiveId]) grouped[objectiveId] = [];
                grouped[objectiveId].push(keyResult);
                return grouped;
            }, {}),
        [keyResults]
    );

    const loadCycles = useCallback(async (preferredCycleId = '') => {
        setIsLoadingCycles(true);
        setErrorMessage('');

        const response = await getOkrCycles();
        const nextCycles = Array.isArray(response?.data) ? response.data : [];

        if (!response) {
            setErrorMessage('No pudimos cargar los ciclos OKR. Intenta nuevamente.');
            setCycles([]);
            setSelectedCycleId('');
            setIsLoadingCycles(false);
            return;
        }

        const activeCycle = defaultCycle(nextCycles);
        const fallbackCycle = nextCycles[0] || null;

        setCycles(nextCycles);
        setSelectedCycleId((previousCycleId) => {
            if (preferredCycleId && nextCycles.some((cycle) => cycle._id === preferredCycleId)) return preferredCycleId;
            if (previousCycleId && nextCycles.some((cycle) => cycle._id === previousCycleId)) return previousCycleId;
            return activeCycle?._id || fallbackCycle?._id || '';
        });
        setIsLoadingCycles(false);
        return nextCycles;
    }, []);

    const loadCycleSummary = useCallback(async (cycleId) => {
        if (!cycleId || !canCreateCycle) {
            setDashboard(null);
            setScoreboard([]);
            setIsLoadingSummary(false);
            return;
        }

        setIsLoadingSummary(true);
        setErrorMessage('');

        const [dashboardResponse, scoreboardResponse] = await Promise.all([getOkrDashboard(cycleId), getOkrScoreboard(cycleId)]);
        if (selectionRef.current !== cycleId) return;

        if (!dashboardResponse || !scoreboardResponse) {
            setErrorMessage('No pudimos cargar el resumen OKR. Revisa la conexión e intenta nuevamente.');
            setDashboard(null);
            setScoreboard([]);
            setIsLoadingSummary(false);
            return;
        }

        setDashboard(dashboardResponse.data || null);
        setScoreboard(Array.isArray(scoreboardResponse.data) ? scoreboardResponse.data : []);
        setIsLoadingSummary(false);
    }, [canCreateCycle]);

    const loadSelectedCycle = useCallback(async (cycleId) => {
        if (!cycleId) return null;
        const response = await getOkrCycle(cycleId);
        const cycle = response?.data || null;
        if (cycle?._id) {
            setCycles((previousCycles) => {
                const exists = previousCycles.some((item) => item._id === cycle._id);
                if (!exists) return [cycle, ...previousCycles];
                return previousCycles.map((item) => (item._id === cycle._id ? cycle : item));
            });
            return cycle;
        }
        return null;
    }, []);

    const loadUsers = useCallback(async () => {
        const response = await handleGetRequest(endPoints.getallUsers, false, false, dispatch);
        const nextUsers = Array.isArray(response?.data) ? response.data : [];
        setUsers(nextUsers.filter((user) => !user.isDeleted && user.isActive !== false));
    }, [dispatch]);

    const loadTeams = useCallback(async (cycleId) => {
        if (!cycleId) {
            setTeams([]);
            return;
        }

        setIsLoadingTeams(true);
        const response = await getOkrTeams(cycleId);
        if (selectionRef.current !== cycleId) return;
        if (!response) {
            setTeams([]);
            setErrorMessage('No pudimos cargar los equipos OKR. Intenta nuevamente.');
            setIsLoadingTeams(false);
            return;
        }

        setTeams(Array.isArray(response?.data) ? response.data : []);
        setIsLoadingTeams(false);
    }, []);

    const loadObjectives = useCallback(async (cycleId) => {
        if (!cycleId) {
            setObjectives([]);
            return;
        }

        setIsLoadingObjectives(true);
        const response = await getOkrObjectives({ cycleId });
        if (selectionRef.current !== cycleId) return;
        if (!response) {
            setObjectives([]);
            setErrorMessage('No pudimos cargar los objetivos OKR. Intenta nuevamente.');
            setIsLoadingObjectives(false);
            return;
        }

        setObjectives(Array.isArray(response?.data) ? response.data : []);
        setIsLoadingObjectives(false);
    }, []);

    const loadKeyResults = useCallback(async (cycleId) => {
        if (!cycleId) {
            setKeyResults([]);
            return;
        }

        setIsLoadingKeyResults(true);
        const response = await getOkrKeyResults({ cycleId });
        if (selectionRef.current !== cycleId) return;
        if (!response) {
            setKeyResults([]);
            setErrorMessage('No pudimos cargar los resultados clave OKR. Intenta nuevamente.');
            setIsLoadingKeyResults(false);
            return;
        }

        setKeyResults(Array.isArray(response?.data) ? response.data : []);
        setIsLoadingKeyResults(false);
    }, []);

    const loadWeeklyEntries = useCallback(async (cycleId, weekNumber) => {
        if (!cycleId || !weekNumber) {
            setWeeklyEntries([]);
            return;
        }

        setIsLoadingWeeklyEntries(true);
        const response = await getOkrWeeklyEntries({ cycleId, weekNumber });
        if (selectionRef.current !== cycleId || Number(weekRef.current) !== Number(weekNumber)) return;
        if (!response) {
            setWeeklyEntries([]);
            setErrorMessage('No pudimos cargar las actualizaciones semanales OKR. Intenta nuevamente.');
            setIsLoadingWeeklyEntries(false);
            return;
        }

        setWeeklyEntries(Array.isArray(response?.data) ? response.data : []);
        setIsLoadingWeeklyEntries(false);
    }, []);

    const loadReportWeeklyEntries = useCallback(async (cycleId) => {
        if (!cycleId) {
            setReportWeeklyEntries([]);
            return;
        }

        setIsLoadingReportWeeklyEntries(true);
        const response = await getOkrWeeklyEntries({ cycleId });
        if (selectionRef.current !== cycleId) return;
        if (!response) {
            setReportWeeklyEntries([]);
            setErrorMessage('No pudimos cargar las actualizaciones para reportes OKR. Intenta nuevamente.');
            setIsLoadingReportWeeklyEntries(false);
            return;
        }

        setReportWeeklyEntries(Array.isArray(response?.data) ? response.data : []);
        setIsLoadingReportWeeklyEntries(false);
    }, []);

    useEffect(() => {
        if (canAccessOkr) {
            loadCycles();
            return;
        }

        setCycles([]);
        setSelectedCycleId('');
        setDashboard(null);
        setScoreboard([]);
        setTeams([]);
        setObjectives([]);
        setKeyResults([]);
        setWeeklyEntries([]);
        setReportWeeklyEntries([]);
        setSelectedWeekNumber(null);
        setErrorMessage('');
        setIsLoadingCycles(false);
    }, [canAccessOkr, loadCycles]);

    useEffect(() => {
        if (!canAccessOkr) return;
        setDashboard(null); setScoreboard([]); setTeams([]); setObjectives([]); setKeyResults([]);
        setWeeklyEntries([]); setReportWeeklyEntries([]); setSelectedWeekNumber(null);
        setWeeklyEntryDialogVisible(false); setKeyResultDialogVisible(false); setObjectiveDialogVisible(false); setTeamDialogVisible(false);
        loadSelectedCycle(selectedCycleId);
        loadCycleSummary(selectedCycleId);
        loadTeams(selectedCycleId);
        loadObjectives(selectedCycleId);
        loadKeyResults(selectedCycleId);
        loadReportWeeklyEntries(selectedCycleId);
    }, [canAccessOkr, loadCycleSummary, loadKeyResults, loadObjectives, loadReportWeeklyEntries, loadSelectedCycle, loadTeams, selectedCycleId]);

    useEffect(() => {
        if (canAccessOkr) loadUsers();
    }, [canAccessOkr, loadUsers]);

    useEffect(() => {
        if (!canAccessOkr) return;
        const weeks = Array.isArray(selectedCycle?.weeks) ? selectedCycle.weeks : [];
        if (!selectedCycleId || !weeks.length) {
            setSelectedWeekNumber(null);
            return;
        }

        setSelectedWeekNumber((previousWeekNumber) => {
            const previousExists = weeks.some((week) => Number(week.weekNumber) === Number(previousWeekNumber));
            if (previousExists) return previousWeekNumber;

            const currentExists = weeks.some((week) => Number(week.weekNumber) === Number(currentWeek?.weekNumber));
            return currentExists ? currentWeek.weekNumber : weeks[0].weekNumber;
        });
    }, [canAccessOkr, currentWeek?.weekNumber, selectedCycle, selectedCycleId]);

    useEffect(() => {
        if (!canAccessOkr) return;
        loadWeeklyEntries(selectedCycleId, selectedWeekNumber);
    }, [canAccessOkr, loadWeeklyEntries, selectedCycleId, selectedWeekNumber]);

    const openCreateCycleDialog = () => {
        setCycleDialogMode('create');
        setCycleDialogVisible(true);
    };

    const openEditCycleDialog = () => {
        if (!selectedCycle) return;
        setCycleDialogMode('edit');
        setCycleDialogVisible(true);
    };

    const closeCycleDialog = () => {
        if (!isSavingCycle) setCycleDialogVisible(false);
    };

    const handleSaveCycle = async (payload) => {
        setIsSavingCycle(true);

        const response =
            cycleDialogMode === 'edit' && selectedCycleId
                ? await dispatch(updateOkrCycle(selectedCycleId, payload))
                : await dispatch(createOkrCycle(payload));
        const savedCycle = getMutationData(response);

        if (!savedCycle?._id) {
            toast.error('No pudimos guardar el ciclo OKR. Intenta nuevamente.');
            setIsSavingCycle(false);
            return;
        }

        toast.success(cycleDialogMode === 'edit' ? 'Ciclo OKR actualizado.' : 'Ciclo OKR creado.');
        setCycleDialogVisible(false);
        setIsSavingCycle(false);

        await loadCycles(savedCycle._id);
        await loadCycleSummary(savedCycle._id);
        await loadSelectedCycle(savedCycle._id);
    };

    const refreshCycleControlsData = async () => {
        if (!selectedCycleId) return;
        await Promise.all([
            loadSelectedCycle(selectedCycleId),
            loadCycleSummary(selectedCycleId),
            loadWeeklyEntries(selectedCycleId, selectedWeekNumber),
            loadReportWeeklyEntries(selectedCycleId)
        ]);
    };

    const handleActivateCycle = async () => {
        if (!selectedCycleId) return;
        const confirmed = window.confirm('¿Activar este ciclo OKR?');
        if (!confirmed) return;

        setIsSavingCycleControl(true);
        const response = await dispatch(updateOkrCycle(selectedCycleId, { status: 'active' }));
        const updatedCycle = getMutationData(response);

        if (!updatedCycle?._id) {
            const backendMessage = getMutationMessage(response);
            toast.error(`No pudimos activar el ciclo${backendMessage ? `: ${backendMessage}` : '. Intenta nuevamente.'}`);
            setIsSavingCycleControl(false);
            return;
        }

        toast.success('Ciclo activado.');
        setIsSavingCycleControl(false);
        await refreshCycleControlsData();
    };

    const handlePreviewCopy = async (payload) => {
        setCopyBusy(true); setCopyError(''); setCopyPreview(null);
        try {
            const response = await dispatch(previewOkrCycleCopy(selectedCycleId, payload));
            const preview = getMutationData(response);
            if (!preview?.expectedFingerprint) { setCopyError(getMutationMessage(response) || 'No se pudo preparar la vista previa.'); return null; }
            setCopyPreview(preview); return preview;
        } finally { setCopyBusy(false); }
    };
    const handleCopyCycle = async (payload) => {
        setCopyBusy(true); setCopyError('');
        try {
            const response = await dispatch(copyOkrCycle(selectedCycleId, payload));
            const copied = getMutationData(response);
            if (!copied?._id) { setCopyError(getMutationMessage(response) || 'No se completó la copia. Puedes volver a intentarlo.'); return; }
            setCopyDialogVisible(false); toast.success('Ciclo preparado sin copiar avances.');
            await loadCycles(copied._id);
        } finally { setCopyBusy(false); }
    };

    const handleCloseCycle = async () => {
        if (!selectedCycleId) return;
        const confirmed = window.confirm('Cerrar este ciclo bloqueará la gestión regular de OKRs. ¿Deseas continuar?');
        if (!confirmed) return;

        setIsSavingCycleControl(true);
        const response = await dispatch(closeOkrCycle(selectedCycleId));
        const closedCycle = getMutationData(response);

        if (!closedCycle?._id) {
            const backendMessage = getMutationMessage(response);
            toast.error(`No pudimos cerrar el ciclo${backendMessage ? `: ${backendMessage}` : '. Intenta nuevamente.'}`);
            setIsSavingCycleControl(false);
            return;
        }

        toast.success('Ciclo cerrado.');
        setIsSavingCycleControl(false);
        await refreshCycleControlsData();
    };

    const handleToggleWeekLock = async (week) => {
        if (!selectedCycleId || !week?.weekNumber) return;
        const nextLocked = !week.isLocked;
        const confirmed = window.confirm(nextLocked ? `¿Bloquear la semana ${week.weekNumber}?` : `¿Desbloquear la semana ${week.weekNumber}?`);
        if (!confirmed) return;

        setLockingWeekNumber(week.weekNumber);
        const response = await dispatch(lockOkrWeek(selectedCycleId, week.weekNumber, { isLocked: nextLocked }));
        const updatedCycle = getMutationData(response);

        if (!updatedCycle?._id) {
            const backendMessage = getMutationMessage(response);
            toast.error(`No pudimos actualizar el bloqueo de la semana${backendMessage ? `: ${backendMessage}` : '. Intenta nuevamente.'}`);
            setLockingWeekNumber(null);
            return;
        }

        toast.success(nextLocked ? 'Semana bloqueada.' : 'Semana desbloqueada.');
        setLockingWeekNumber(null);
        await refreshCycleControlsData();
    };

    const openCreateTeamDialog = () => {
        setSelectedTeam(null);
        setTeamDialogVisible(true);
    };

    const openEditTeamDialog = (team) => {
        setSelectedTeam(team);
        setTeamDialogVisible(true);
    };

    const closeTeamDialog = () => {
        if (!isSavingTeam) setTeamDialogVisible(false);
    };

    const refreshSelectedCycleData = async () => {
        if (!selectedCycleId) return;
        await Promise.all([loadTeams(selectedCycleId), loadObjectives(selectedCycleId), loadKeyResults(selectedCycleId), loadCycleSummary(selectedCycleId)]);
    };

    const handleSaveTeam = async (payload) => {
        if (!selectedCycleId) {
            toast.error('Selecciona un ciclo OKR antes de agregar equipos.');
            return;
        }

        setIsSavingTeam(true);
        const response = selectedTeam?._id
            ? await dispatch(updateOkrTeam(selectedTeam._id, payload))
            : await dispatch(createOkrTeam({ ...payload, cycleId: selectedCycleId }));
        const savedTeam = getMutationData(response);

        if (!savedTeam?._id) {
            const backendMessage = getMutationMessage(response);
            toast.error(`No pudimos guardar el equipo${backendMessage ? `: ${backendMessage}` : '. Intenta nuevamente.'}`);
            setIsSavingTeam(false);
            return;
        }

        toast.success(selectedTeam?._id ? 'Equipo actualizado.' : 'Equipo agregado.');
        setTeamDialogVisible(false);
        setSelectedTeam(null);
        setIsSavingTeam(false);
        await refreshSelectedCycleData();
    };

    const handleDeleteTeam = async (team) => {
        if (!team?._id) return;
        const confirmed = window.confirm(`¿Desactivar el equipo "${team.name}" de este ciclo OKR?`);
        if (!confirmed) return;

        const response = await dispatch(deleteOkrTeam(team._id));
        const deletedTeam = getMutationData(response);
        if (!deletedTeam?._id) {
            const backendMessage = getMutationMessage(response);
            toast.error(`No pudimos desactivar el equipo${backendMessage ? `: ${backendMessage}` : '. Intenta nuevamente.'}`);
            return;
        }

        toast.success('Equipo desactivado.');
        await refreshSelectedCycleData();
    };

    const openCreateObjectiveDialog = (teamId = '') => {
        setSelectedObjective(null);
        setObjectiveTeamId(teamId);
        setObjectiveDialogVisible(true);
    };

    const openEditObjectiveDialog = (objective) => {
        setSelectedObjective(objective);
        setObjectiveTeamId(getUserId(objective?.teamId));
        setObjectiveDialogVisible(true);
    };

    const closeObjectiveDialog = () => {
        if (!isSavingObjective) {
            setObjectiveDialogVisible(false);
            setSelectedObjective(null);
            setObjectiveTeamId('');
        }
    };

    const refreshObjectivesAndSummary = async () => {
        if (!selectedCycleId) return;
        await Promise.all([loadObjectives(selectedCycleId), loadKeyResults(selectedCycleId), loadCycleSummary(selectedCycleId), loadTeams(selectedCycleId)]);
    };

    const handleSaveObjective = async (payload) => {
        if (!selectedCycleId) {
            toast.error('Selecciona un ciclo OKR antes de agregar objetivos.');
            return;
        }

        setIsSavingObjective(true);
        const objectivePayload = selectedObjective?._id
            ? {
                  title: payload.title,
                  description: payload.description,
                  responsibleUserIds: payload.responsibleUserIds,
                  totalWeight: payload.totalWeight
              }
            : { ...payload, cycleId: selectedCycleId };
        const response = selectedObjective?._id
            ? await dispatch(updateOkrObjective(selectedObjective._id, objectivePayload))
            : await dispatch(createOkrObjective(objectivePayload));
        const savedObjective = getMutationData(response);

        if (!savedObjective?._id) {
            const backendMessage = getMutationMessage(response);
            toast.error(`No pudimos guardar el objetivo${backendMessage ? `: ${backendMessage}` : '. Intenta nuevamente.'}`);
            setIsSavingObjective(false);
            return;
        }

        toast.success(selectedObjective?._id ? 'Objetivo actualizado.' : 'Objetivo agregado.');
        setObjectiveDialogVisible(false);
        setSelectedObjective(null);
        setObjectiveTeamId('');
        setIsSavingObjective(false);
        await refreshObjectivesAndSummary();
    };

    const handleDeleteObjective = async (objective) => {
        if (!objective?._id) return;
        const confirmed = window.confirm(`¿Desactivar el objetivo "${objective.title}" de este ciclo OKR?`);
        if (!confirmed) return;

        const response = await dispatch(deleteOkrObjective(objective._id));
        const deletedObjective = getMutationData(response);
        if (!deletedObjective?._id) {
            const backendMessage = getMutationMessage(response);
            toast.error(`No pudimos desactivar el objetivo${backendMessage ? `: ${backendMessage}` : '. Intenta nuevamente.'}`);
            return;
        }

        toast.success('Objetivo desactivado.');
        await refreshObjectivesAndSummary();
    };

    const openCreateKeyResultDialog = (team, objective) => {
        setSelectedKeyResult(null);
        setKeyResultContext({ team, objective });
        setKeyResultDialogVisible(true);
    };

    const openEditKeyResultDialog = (keyResult, team, objective) => {
        setSelectedKeyResult(keyResult);
        setKeyResultContext({ team, objective });
        setKeyResultDialogVisible(true);
    };

    const closeKeyResultDialog = () => {
        if (!isSavingKeyResult) {
            setKeyResultDialogVisible(false);
            setSelectedKeyResult(null);
            setKeyResultContext({ team: null, objective: null });
        }
    };

    const refreshKeyResultsAndSummary = async () => {
        if (!selectedCycleId) return;
        await Promise.all([
            loadKeyResults(selectedCycleId),
            loadCycleSummary(selectedCycleId),
            loadWeeklyEntries(selectedCycleId, selectedWeekNumber),
            loadReportWeeklyEntries(selectedCycleId)
        ]);
    };

    const handleSaveKeyResult = async (payload) => {
        if (!selectedCycleId || !keyResultContext?.team?._id || !keyResultContext?.objective?._id) {
            toast.error('Selecciona un objetivo OKR antes de agregar resultados clave.');
            return;
        }

        setIsSavingKeyResult(true);
        const ownerId = payload.ownerId || payload.pointOwnerUserId || payload.updateResponsibleUserId;
        const keyResultPayload = {
            ownerId,
            pointOwnerType: 'user',
            pointOwnerUserId: ownerId,
            pointOwnerName: '',
            updateResponsibleUserId: ownerId,
            title: payload.title,
            description: payload.description || '',
            weight: Number(payload.weight),
            weeklyTarget: payload.weeklyTarget === null || payload.weeklyTarget === undefined || payload.weeklyTarget === '' ? null : Number(payload.weeklyTarget),
            unit: payload.unit || '',
            calculationType: payload.calculationType || 'manual_value',
            scoringMethod: payload.scoringMethod,
            allowOverTarget: Boolean(payload.allowOverTarget),
            maxMultiplier: payload.maxMultiplier === null || payload.maxMultiplier === undefined || payload.maxMultiplier === '' ? null : Number(payload.maxMultiplier),
            automationConfig: payload.automationConfig || { sourceType: 'manual', isEnabled: false }
        };
        const createPayload = {
            ...keyResultPayload,
            cycleId: selectedCycleId,
            teamId: keyResultContext.team._id,
            objectiveId: keyResultContext.objective._id
        };
        const response = selectedKeyResult?._id
            ? await dispatch(updateOkrKeyResult(selectedKeyResult._id, keyResultPayload))
            : await dispatch(createOkrKeyResult(createPayload));
        const savedKeyResult = getMutationData(response);

        if (!hasMutationSucceeded(response, savedKeyResult)) {
            const backendMessage = getMutationMessage(response);
            toast.error(`No pudimos guardar el resultado clave${backendMessage ? `: ${backendMessage}` : '. Intenta nuevamente.'}`);
            setIsSavingKeyResult(false);
            return;
        }

        toast.success(selectedKeyResult?._id ? 'Resultado clave actualizado.' : 'Resultado clave agregado.');
        setKeyResultDialogVisible(false);
        setSelectedKeyResult(null);
        setKeyResultContext({ team: null, objective: null });
        setIsSavingKeyResult(false);
        await refreshKeyResultsAndSummary();
    };

    const handleDeleteKeyResult = async (keyResult) => {
        if (!keyResult?._id) return;
        const confirmed = window.confirm(`¿Desactivar el resultado clave "${keyResult.title}"?`);
        if (!confirmed) return;

        const response = await dispatch(deleteOkrKeyResult(keyResult._id));
        const deletedKeyResult = getMutationData(response);
        if (!deletedKeyResult?._id) {
            const backendMessage = getMutationMessage(response);
            toast.error(`No pudimos desactivar el resultado clave${backendMessage ? `: ${backendMessage}` : '. Intenta nuevamente.'}`);
            return;
        }

        toast.success('Resultado clave desactivado.');
        await refreshKeyResultsAndSummary();
    };

    const openWeeklyEntryDialog = ({ keyResult, team, objective, entry, locked, weekNumber }) => {
        if (selectedCycle?.status === 'draft') { toast.info('Activa el ciclo antes de registrar avances.'); return; }
        setWeeklyEntryContext({ keyResult, team, objective, entry, locked: Boolean(locked), weekNumber: weekNumber || selectedWeekNumber });
        setWeeklyEntryDialogVisible(true);
    };

    const closeWeeklyEntryDialog = () => {
        if (!isSavingWeeklyEntry) {
            setWeeklyEntryDialogVisible(false);
            setWeeklyEntryContext({ keyResult: null, team: null, objective: null, entry: null, locked: false, weekNumber: null });
        }
    };

    const handleSaveWeeklyEntry = async (payload) => {
        const targetWeekNumber = weeklyEntryContext.weekNumber || selectedWeekNumber;
        if (!weeklyEntryContext?.keyResult?._id || !targetWeekNumber) {
            toast.error('Selecciona una semana y un resultado clave antes de guardar.');
            return;
        }

        setIsSavingWeeklyEntry(true);
        const updatePayload = { ...payload };
        delete updatePayload.keyResultId;
        delete updatePayload.weekNumber;

        const response = weeklyEntryContext.entry?._id
            ? await dispatch(updateOkrWeeklyEntry(weeklyEntryContext.entry._id, updatePayload))
            : await dispatch(createOrUpdateOkrWeeklyEntry(payload));
        const savedEntry = getMutationData(response);

        if (!savedEntry?._id) {
            const backendMessage = getMutationMessage(response);
            toast.error(`No pudimos guardar la actualización semanal${backendMessage ? `: ${backendMessage}` : '. Intenta nuevamente.'}`);
            setIsSavingWeeklyEntry(false);
            return;
        }

        toast.success(payload.status === 'submitted' ? 'Actualización semanal enviada.' : 'Borrador semanal guardado.');
        setWeeklyEntryDialogVisible(false);
        setWeeklyEntryContext({ keyResult: null, team: null, objective: null, entry: null, locked: false, weekNumber: null });
        setIsSavingWeeklyEntry(false);
        await Promise.all([loadWeeklyEntries(selectedCycleId, targetWeekNumber), loadReportWeeklyEntries(selectedCycleId), loadCycleSummary(selectedCycleId)]);
    };

    const handleSyncWeeklyEntryFromSource = async () => {
        const targetWeekNumber = weeklyEntryContext.weekNumber || selectedWeekNumber;
        if (!weeklyEntryContext?.keyResult?._id || !targetWeekNumber) {
            toast.error('Selecciona una semana y un resultado clave antes de sincronizar.');
            return;
        }

        setIsSavingWeeklyEntry(true);
        const response = await dispatch(syncOkrKeyResultWeek(weeklyEntryContext.keyResult._id, { weekNumber: targetWeekNumber }));
        const syncedEntry = getMutationData(response);

        if (!syncedEntry?._id) {
            const backendMessage = getMutationMessage(response);
            toast.error(`No pudimos sincronizar el dato${backendMessage ? `: ${backendMessage}` : '. Intenta nuevamente.'}`);
            setIsSavingWeeklyEntry(false);
            return;
        }

        toast.success('Dato sincronizado desde QuickBooks.');
        setWeeklyEntryDialogVisible(false);
        setWeeklyEntryContext({ keyResult: null, team: null, objective: null, entry: null, locked: false, weekNumber: null });
        setIsSavingWeeklyEntry(false);
        await Promise.all([loadWeeklyEntries(selectedCycleId, targetWeekNumber), loadReportWeeklyEntries(selectedCycleId), loadCycleSummary(selectedCycleId)]);
    };

    const refreshWeeklyReviewData = async () => {
        await Promise.all([loadWeeklyEntries(selectedCycleId, selectedWeekNumber), loadReportWeeklyEntries(selectedCycleId), loadCycleSummary(selectedCycleId)]);
    };

    const handleApproveWeeklyEntry = async (entry) => {
        if (!entry?._id) return false;

        setReviewingEntryId(entry._id);
        const response = await dispatch(approveOkrWeeklyEntry(entry._id));
        const approvedEntry = getMutationData(response);

        if (!approvedEntry?._id) {
            const backendMessage = getMutationMessage(response);
            toast.error(`No pudimos aprobar la actualización${backendMessage ? `: ${backendMessage}` : '. Intenta nuevamente.'}`);
            setReviewingEntryId('');
            return false;
        }

        toast.success('Actualización aprobada.');
        setReviewingEntryId('');
        await refreshWeeklyReviewData();
        return true;
    };

    const handleRejectWeeklyEntry = async (entry, payload) => {
        if (!entry?._id) return false;

        setReviewingEntryId(entry._id);
        const response = await dispatch(rejectOkrWeeklyEntry(entry._id, payload));
        const rejectedEntry = getMutationData(response);

        if (!rejectedEntry?._id) {
            const backendMessage = getMutationMessage(response);
            toast.error(`No pudimos rechazar la actualización${backendMessage ? `: ${backendMessage}` : '. Intenta nuevamente.'}`);
            setReviewingEntryId('');
            return false;
        }

        toast.success('Actualización rechazada.');
        setReviewingEntryId('');
        await refreshWeeklyReviewData();
        return true;
    };

    const renderTeamMemberSummary = (team) => {
        const numberOfWeeks = Number(selectedCycle?.numberOfWeeks || 0);
        const pointsBase = Number(selectedCycle?.pointsBase || 1000);
        const expectedWeeklyFullTarget = numberOfWeeks > 0 ? pointsBase / numberOfWeeks : 0;
        const activeTeamKeyResults = keyResults.filter((keyResult) => belongsToOkrTeam(keyResult, team) && keyResult?.isActive !== false);
        const membersById = new Map((Array.isArray(team?.memberIds) ? team.memberIds : []).map((member) => [getUserId(member), member]));
        activeTeamKeyResults.forEach((keyResult) => {
            const pointOwnerId = getPointOwnerId(keyResult);
            if (pointOwnerId && !membersById.has(pointOwnerId)) {
                membersById.set(pointOwnerId, {
                    _id: pointOwnerId,
                    name: getPointOwnerName(keyResult, findUserById)
                });
            }
        });
        const members = Array.from(membersById.values());
        if (!members.length) return null;
        const getAssignmentStatus = (assignedPoints) => {
            if (assignedPoints > pointsBase) return { label: 'Sobreasignado', severity: 'danger' };
            if (assignedPoints === pointsBase) return { label: 'Completo', severity: 'success' };
            return { label: 'Incompleto', severity: 'warning' };
        };

        return (
            <div className="surface-50 border-round p-3 mb-3">
                <div className="flex flex-column md:flex-row md:align-items-start md:justify-content-between gap-2 mb-3">
                    <div>
                        <div className="font-semibold">Resumen por colaborador</div>
                        <div className="text-sm text-color-secondary">Cada persona debe completar {formatNumber(pointsBase)} puntos en el ciclo.</div>
                    </div>
                    <Tag value={`Meta semanal completa: ${formatNumber(expectedWeeklyFullTarget)} pts`} severity="info" />
                </div>
                <div className="grid">
                    {members.map((member) => {
                        const memberId = getUserId(member);
                        const assignedPoints = activeTeamKeyResults
                            .filter((keyResult) => getPointOwnerId(keyResult) === memberId)
                            .reduce((total, keyResult) => total + Number(keyResult.weight || 0), 0);
                        const configuredWeeklyPoints = numberOfWeeks > 0 ? assignedPoints / numberOfWeeks : 0;
                        const status = getAssignmentStatus(assignedPoints);

                        return (
                            <div className="col-12 md:col-6 xl:col-4" key={memberId}>
                                <div className="border-1 surface-border border-round p-2 h-full">
                                    <div className="flex align-items-start justify-content-between gap-2 mb-2">
                                        <div className="font-medium">{member?.name || getUserName(member)}</div>
                                        <Tag value={status.label} severity={status.severity} />
                                    </div>
                                    <div className="text-sm text-color-secondary">{formatNumber(assignedPoints)} / {formatNumber(pointsBase)} pts asignados</div>
                                    <div className="text-sm text-color-secondary">Pts/semana configurados: {formatNumber(configuredWeeklyPoints)}</div>
                                    <div className="text-sm text-color-secondary">Meta semanal completa: {formatNumber(expectedWeeklyFullTarget)} pts</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderTeamAssignmentMessages = (team) => {
        const pointsBase = Number(selectedCycle?.pointsBase || 1000);
        const activeTeamKeyResults = keyResults.filter((keyResult) => belongsToOkrTeam(keyResult, team) && keyResult?.isActive !== false);
        const membersById = new Map((Array.isArray(team?.memberIds) ? team.memberIds : []).map((member) => [getUserId(member), member]));
        activeTeamKeyResults.forEach((keyResult) => {
            const pointOwnerId = getPointOwnerId(keyResult);
            if (pointOwnerId && !membersById.has(pointOwnerId)) {
                membersById.set(pointOwnerId, {
                    _id: pointOwnerId,
                    name: getPointOwnerName(keyResult, findUserById)
                });
            }
        });
        const members = Array.from(membersById.values());
        if (!members.length) return null;
        const messages = members.map((member) => {
            const memberId = getUserId(member);
            const assignedPoints = activeTeamKeyResults
                .filter((keyResult) => getPointOwnerId(keyResult) === memberId)
                .reduce((total, keyResult) => total + Number(keyResult.weight || 0), 0);
            const severity = assignedPoints > pointsBase ? 'danger' : assignedPoints === pointsBase ? 'success' : 'warning';

            return {
                memberId,
                severity,
                text: `${member?.name || getUserName(member)} tiene ${formatNumber(assignedPoints)} de ${formatNumber(pointsBase)} puntos asignados en este ciclo.`
            };
        });

        return (
            <div className="flex flex-column gap-2 mb-3">
                {messages.map((message) => (
                    <div className="flex align-items-center gap-2 text-sm" key={message.memberId}>
                        <i className={`pi ${message.severity === 'success' ? 'pi-check-circle text-green-600' : message.severity === 'danger' ? 'pi-exclamation-triangle text-red-600' : 'pi-info-circle text-yellow-700'}`} />
                        <span>{message.text}</span>
                    </div>
                ))}
            </div>
        );
    };

    const weekLockedBody = (rowData) => (
        <Tag value={rowData?.isLocked ? 'Semana bloqueada' : 'Semana abierta'} severity={rowData?.isLocked ? 'danger' : 'success'} />
    );

    const weekActionsBody = (rowData) => (
        <Button
            label={rowData?.isLocked ? 'Desbloquear semana' : 'Bloquear semana'}
            icon={rowData?.isLocked ? 'pi pi-unlock' : 'pi pi-lock'}
            className={`p-button-sm p-button-outlined ${rowData?.isLocked ? '' : 'p-button-warning'}`}
            onClick={() => handleToggleWeekLock(rowData)}
            loading={lockingWeekNumber === rowData?.weekNumber}
            disabled={isSavingCycleControl}
        />
    );

    const findUserById = (userId) => users.find((user) => String(getUserId(user)) === String(getUserId(userId)));

    const objectiveTitleBody = (rowData) => (
        <div>
            <div className="font-semibold">{rowData?.title || '-'}</div>
            {rowData?.description ? <div className="text-xs text-color-secondary mt-1">{rowData.description}</div> : null}
        </div>
    );

    const objectiveResponsiblesBody = (rowData) => {
        const responsibles = Array.isArray(rowData?.responsibleUserIds) ? rowData.responsibleUserIds : [];
        if (!responsibles.length) return <span className="text-color-secondary">Sin responsables</span>;
        return (
            <div className="flex flex-wrap gap-2">
                {responsibles.slice(0, 3).map((responsible) => {
                    const user = findUserById(responsible) || responsible;
                    return <Tag key={getUserId(responsible)} value={getUserName(user)} severity="info" />;
                })}
                {responsibles.length > 3 ? <Tag value={`+${responsibles.length - 3}`} severity="secondary" /> : null}
            </div>
        );
    };

    const objectiveStatusBody = (rowData) => (
        <Tag value={rowData?.isActive === false ? 'Inactivo' : 'Activo'} severity={rowData?.isActive === false ? 'warning' : 'success'} />
    );

    const keyResultOwnerBody = (rowData) => getPointOwnerName(rowData, findUserById);

    const keyResultTargetBody = (rowData) => {
        if (rowData?.weeklyTarget === null || rowData?.weeklyTarget === undefined || rowData?.weeklyTarget === '') return '-';
        return formatNumber(rowData.weeklyTarget);
    };

    const keyResultWeeklyPointsBody = (rowData) => {
        const numberOfWeeks = Number(selectedCycle?.numberOfWeeks || 0);
        if (!numberOfWeeks) return '-';
        return formatNumber(Number(rowData?.weight || 0) / numberOfWeeks);
    };

    const keyResultCalculationTypeBody = (rowData) => getCalculationTypeLabel(rowData?.calculationType, rowData);

    const keyResultOverTargetBody = (rowData) => (
        <Tag value={rowData?.allowOverTarget === false ? 'No' : 'Sí'} severity={rowData?.allowOverTarget === false ? 'warning' : 'success'} />
    );

    const keyResultStatusBody = (rowData) => (
        <Tag value={rowData?.isActive === false ? 'Inactivo' : 'Activo'} severity={rowData?.isActive === false ? 'warning' : 'success'} />
    );

    const keyResultActionsBody = (team, objective) => (rowData) => (
        <div className="flex flex-wrap gap-2">
            <Button
                label="Editar"
                icon="pi pi-pencil"
                className="p-button-sm p-button-outlined"
                title="Editar resultado clave medible"
                aria-label={`Editar resultado clave medible ${rowData?.title || ''}`}
                onClick={() => openEditKeyResultDialog(rowData, team, objective)}
            />
            <Button
                label="Desactivar"
                icon="pi pi-trash"
                className="p-button-sm p-button-outlined p-button-danger"
                title="Desactivar resultado clave medible"
                aria-label={`Desactivar resultado clave medible ${rowData?.title || ''}`}
                onClick={() => handleDeleteKeyResult(rowData)}
            />
        </div>
    );

    const getExportFilename = (name) => `okr_${name}_${safeFilenamePart(selectedCycle?.name)}.csv`;

    const exportScoreboardCsv = () => {
        const rows = scoreboard.map((row, index) => ({
            ranking: index + 1,
            teamName: row?.team?.name || '-',
            normalizedTeamPoints: row?.normalizedTeamPoints ?? row?.accumulatedPoints ?? 0,
            teamProgressPercentage: row?.teamProgressPercentage || 0,
            rawTotalPoints: row?.rawTotalPoints || 0,
            members: Array.isArray(row?.members) ? row.members.length : 0
        }));

        downloadCsv(
            getExportFilename('scoreboard'),
            [
                { header: 'Ranking', value: (row) => row.ranking },
                { header: 'Equipo', value: (row) => row.teamName },
                { header: 'Puntaje normalizado', value: (row) => formatNumber(row.normalizedTeamPoints) },
                { header: '% avance', value: (row) => formatNumber(row.teamProgressPercentage) },
                { header: 'Puntos brutos', value: (row) => formatNumber(row.rawTotalPoints) },
                { header: 'Colaboradores', value: (row) => row.members }
            ],
            rows
        );
    };

    const exportPeopleCsv = () => {
        const pointsBase = Number(selectedCycle?.pointsBase || 1000);
        const numberOfWeeks = Number(selectedCycle?.numberOfWeeks || 0);
        const rows = [];

        teams.forEach((team) => {
            const members = Array.isArray(team?.memberIds) ? team.memberIds : [];
            members.forEach((member) => {
                const userId = getUserId(member);
                const memberKeyResults = keyResults.filter(
                    (keyResult) => keyResult?.isActive !== false && belongsToOkrTeam(keyResult, team) && getPointOwnerId(keyResult) === userId
                );
                const assignedPoints = memberKeyResults.reduce((total, keyResult) => total + Number(keyResult.weight || 0), 0);
                const personEntries = reportWeeklyEntries.filter((entry) => getPointOwnerId(entry) === userId && belongsToOkrTeam(entry, team));
                const accumulatedPoints = personEntries
                    .filter((entry) => entry.status === 'submitted' || entry.status === 'approved')
                    .reduce((total, entry) => total + Number(entry.finalPoints || 0), 0);
                const assignment = assignedPoints > pointsBase ? 'Sobreasignado' : assignedPoints === pointsBase ? 'Completo' : 'Incompleto';

                rows.push({
                    personName: getUserName(member),
                    teamName: team.name,
                    assignedPoints,
                    accumulatedPoints,
                    progressPercentage: pointsBase > 0 ? (accumulatedPoints / pointsBase) * 100 : 0,
                    configuredWeeklyPoints: numberOfWeeks > 0 ? assignedPoints / numberOfWeeks : 0,
                    fullWeeklyTarget: numberOfWeeks > 0 ? pointsBase / numberOfWeeks : 0,
                    assignment
                });
            });
        });

        downloadCsv(
            getExportFilename('personas'),
            [
                { header: 'Persona', value: (row) => row.personName },
                { header: 'Equipo', value: (row) => row.teamName },
                { header: 'Puntos asignados', value: (row) => formatNumber(row.assignedPoints) },
                { header: 'Puntos acumulados', value: (row) => formatNumber(row.accumulatedPoints) },
                { header: '% avance', value: (row) => formatNumber(row.progressPercentage) },
                { header: 'Puntos/semana configurados', value: (row) => formatNumber(row.configuredWeeklyPoints) },
                { header: 'Meta semanal completa', value: (row) => formatNumber(row.fullWeeklyTarget) },
                { header: 'Estado de asignación', value: (row) => row.assignment }
            ],
            rows
        );
    };

    const exportWeeklyEntriesCsv = () => {
        const rows = reportWeeklyEntries.map((entry) => {
            const keyResult = keyResults.find((item) => getUserId(item) === getUserId(entry?.keyResultId)) || {};
            const objective = objectives.find((item) => getUserId(item) === getUserId(entry?.objectiveId) || getUserId(item) === getUserId(keyResult?.objectiveId)) || {};
            const team = teams.find((item) => getUserId(item) === getUserId(entry?.teamId) || getUserId(item) === getUserId(keyResult?.teamId)) || {};
            const owner = findUserById(entry?.ownerId) || findUserById(keyResult?.ownerId) || entry?.ownerId || keyResult?.ownerId;
            const input = entry?.calculationInput || {};

            return {
                weekNumber: entry.weekNumber,
                ownerName: getUserName(owner),
                teamName: team.name || '-',
                objectiveTitle: objective.title || '-',
                keyResultTitle: keyResult.title || '-',
                calculationType: getCalculationTypeLabel(keyResult.calculationType, keyResult),
                actualValue: keyResult.calculationType === 'action_completed'
                    ? Number(entry.actualValue) === 1
                        ? 'Completada'
                        : 'No completada'
                    : entry.actualValue,
                forecast: input.forecast,
                real: input.real,
                variance: input.variance,
                calculatedPoints: entry.calculatedPoints,
                finalPoints: entry.finalPoints,
                status: entry.status,
                rejectionReason: entry.rejectionReason,
                updatedAt: entry.updatedAt
            };
        });

        downloadCsv(
            getExportFilename('actualizaciones'),
            [
                { header: 'Semana', value: (row) => row.weekNumber },
                { header: 'Persona', value: (row) => row.ownerName },
                { header: 'Equipo', value: (row) => row.teamName },
                { header: 'Objetivo', value: (row) => row.objectiveTitle },
                { header: 'Resultado clave', value: (row) => row.keyResultTitle },
                { header: 'Tipo de captura', value: (row) => row.calculationType },
                { header: 'Valor real', value: (row) => typeof row.actualValue === 'string' ? row.actualValue : formatNumber(row.actualValue) },
                { header: 'Forecast', value: (row) => row.forecast ?? '' },
                { header: 'Real', value: (row) => row.real ?? '' },
                { header: 'Varianza %', value: (row) => row.variance ?? '' },
                { header: 'Puntos calculados', value: (row) => formatNumber(row.calculatedPoints) },
                { header: 'Puntos finales', value: (row) => formatNumber(row.finalPoints) },
                { header: 'Estado', value: (row) => row.status },
                { header: 'Motivo de rechazo', value: (row) => row.rejectionReason || '' },
                { header: 'Fecha de actualización', value: (row) => formatDate(row.updatedAt) }
            ],
            rows
        );
    };

    const renderExportSection = () => (
        <div className="surface-card border-round p-3 shadow-1">
            <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3">
                <div>
                    <h3 className="m-0">Exportaciones</h3>
                    <p className="text-color-secondary mt-2 mb-0">Descarga CSV rápidos del ciclo seleccionado sin abrir una vista de reportes separada.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button label="Exportar scoreboard CSV" icon="pi pi-download" className="p-button-outlined" onClick={exportScoreboardCsv} disabled={!selectedCycleId} />
                    <Button label="Exportar personas CSV" icon="pi pi-download" className="p-button-outlined" onClick={exportPeopleCsv} disabled={!selectedCycleId} />
                    <Button label="Exportar actualizaciones CSV" icon="pi pi-download" className="p-button-outlined" onClick={exportWeeklyEntriesCsv} disabled={!selectedCycleId} />
                </div>
            </div>
        </div>
    );

    const renderTeamWorkspace = () => {
        if (!selectedCycleId && !isLoadingCycles) {
            return (
                <EmptyState
                    title="Selecciona o crea un ciclo OKR para configurar equipos."
                    action={canCreateCycle ? <Button label="Nuevo Ciclo" icon="pi pi-plus" onClick={openCreateCycleDialog} className="mt-3" /> : null}
                />
            );
        }

        if (selectedCycleId && !isLoadingTeams && !teams.length) {
            return (
                <EmptyState
                    title="No hay equipos configurados para este ciclo. Agrega un equipo para comenzar."
                    action={canCreateCycle ? <Button label="Agregar Equipo" icon="pi pi-plus" onClick={openCreateTeamDialog} className="mt-3" /> : null}
                />
            );
        }

        return (
            <div className="flex flex-column gap-3">
                <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3">
                    <div>
                    <h3 className="m-0">Equipos del ciclo</h3>
                    <p className="text-color-secondary mt-2 mb-0">Jerarquía: Equipo → Objetivo principal → Resultado clave medible.</p>
                    </div>
                    {canCreateCycle ? <Button label="Agregar Equipo" icon="pi pi-plus" onClick={openCreateTeamDialog} disabled={!selectedCycleId} /> : null}
                </div>

                {isLoadingObjectives ? <div className="surface-card border-round p-3 shadow-1 text-color-secondary">Cargando objetivos...</div> : null}

                {teams.map((team) => {
                    const teamObjectives = objectivesByTeamId[team._id] || [];
                    const canManageThisTeam = canManageTeamConfiguration(team);
                    return (
                        <div className="surface-card border-round p-3 shadow-1" key={team._id}>
                            <div className="flex flex-column lg:flex-row lg:align-items-start lg:justify-content-between gap-3 mb-3">
                                <div>
                                    <div className="flex align-items-center flex-wrap gap-2 mb-2">
                                        <h3 className="m-0">{team.name}</h3>
                                        <Tag value={team.scoringMode === 'sum' ? 'Suma' : 'Promedio'} severity="info" />
                                    </div>
                                    <div className="text-sm text-color-secondary mb-2">Manager OKR: {getUserName(team.managerId)}</div>
                                    <div className="flex align-items-center flex-wrap gap-2">
                                        <span className="text-sm text-color-secondary">Colaboradores:</span>
                                        {Array.isArray(team.memberIds) && team.memberIds.length ? (
                                            team.memberIds.slice(0, 5).map((member) => <Tag key={getUserId(member)} value={getUserName(member)} severity="secondary" />)
                                        ) : (
                                            <span className="text-sm text-color-secondary">Sin colaboradores</span>
                                        )}
                                        {Array.isArray(team.memberIds) && team.memberIds.length > 5 ? <Tag value={`+${team.memberIds.length - 5}`} severity="secondary" /> : null}
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    {canManageThisTeam && !team.isManagementGroup ? (
                                        <Button label="Agregar objetivo principal" icon="pi pi-plus" className="p-button-sm" onClick={() => openCreateObjectiveDialog(team._id)} />
                                    ) : null}
                                    {canManageThisTeam ? (
                                        <>
                                            <Button label="Editar equipo" icon="pi pi-pencil" className="p-button-sm p-button-outlined" onClick={() => openEditTeamDialog(team)} />
                                            {canCreateCycle ? <Button label="Desactivar equipo" icon="pi pi-trash" className="p-button-sm p-button-outlined p-button-danger" onClick={() => handleDeleteTeam(team)} /> : null}
                                        </>
                                    ) : null}
                                </div>
                            </div>

                            {!canManageThisTeam ? (
                                <div className="surface-50 border-round p-3 mb-3 text-sm text-color-secondary">
                                    Solo el Manager OKR o un administrador puede editar la configuración del equipo.
                                </div>
                            ) : null}

                            {renderTeamMemberSummary(team)}
                            {renderTeamAssignmentMessages(team)}

                            {teamObjectives.length ? (
                                <div className="flex flex-column gap-3">
                                    {teamObjectives.map((objective) => {
                                        const objectiveKeyResults = keyResultsByObjectiveId[objective._id] || [];

                                        return (
                                            <div className="border-1 surface-border border-round p-3" key={objective._id}>
                                                <div className="flex flex-column lg:flex-row lg:align-items-start lg:justify-content-between gap-3 mb-3">
                                                    <div>
                                                        {objectiveTitleBody(objective)}
                                                        <div className="flex flex-wrap gap-2 mt-2">
                                                            {objectiveResponsiblesBody(objective)}
                                                            <Tag value={`${formatNumber(objective.totalWeight)} pts`} severity="info" />
                                                            {objectiveStatusBody(objective)}
                                                        </div>
                                                    </div>
                                                    {canManageThisTeam ? (
                                                        <div className="flex flex-wrap gap-2">
                                                            <Button label="Agregar resultado clave medible" icon="pi pi-plus" className="p-button-sm" onClick={() => openCreateKeyResultDialog(team, objective)} />
                                                            <Button label="Editar objetivo principal" icon="pi pi-pencil" className="p-button-sm p-button-outlined" onClick={() => openEditObjectiveDialog(objective)} />
                                                            <Button label="Desactivar" icon="pi pi-trash" className="p-button-sm p-button-outlined p-button-danger" onClick={() => handleDeleteObjective(objective)} />
                                                        </div>
                                                    ) : null}
                                                </div>

                                                <h4 className="mt-0 mb-2">Resultados clave medibles</h4>
                                                {objectiveKeyResults.length ? (
                                                    <DataTable value={objectiveKeyResults} loading={isLoadingKeyResults} responsiveLayout="scroll" size="small">
                                                        <Column field="title" header="Resultado clave medible" sortable style={{ minWidth: '220px' }} />
                                                        <Column header="Persona responsable" body={keyResultOwnerBody} style={{ minWidth: '190px' }} />
                                                        <Column field="weight" header="Peso" body={(rowData) => formatNumber(rowData?.weight)} sortable style={{ minWidth: '100px' }} />
                                                        <Column header="Puntos/semana" body={keyResultWeeklyPointsBody} style={{ minWidth: '130px' }} />
                                                        <Column header="Meta esperada" body={keyResultTargetBody} style={{ minWidth: '130px' }} />
                                                        <Column field="unit" header="Unidad" style={{ minWidth: '110px' }} />
                                                        <Column header="Tipo de captura" body={keyResultCalculationTypeBody} style={{ minWidth: '180px' }} />
                                                        <Column header="Método de cálculo" body={(rowData) => getScoringMethodLabel(rowData?.scoringMethod)} style={{ minWidth: '170px' }} />
                                                        <Column header="Permite exceder meta" body={keyResultOverTargetBody} style={{ minWidth: '160px' }} />
                                                        <Column header="Estado" body={keyResultStatusBody} style={{ minWidth: '110px' }} />
                                                        {canManageThisTeam ? <Column header="Acciones" body={keyResultActionsBody(team, objective)} style={{ minWidth: '230px' }} /> : null}
                                                    </DataTable>
                                                ) : (
                                                    <div className="border-1 surface-border border-round p-3 text-color-secondary">Este objetivo principal aún no tiene resultados clave medibles.</div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="border-1 surface-border border-round p-3 text-color-secondary">{team.isManagementGroup ? 'Consulta los OKR existentes seleccionando Gerencia en Mis OKRs, Dashboard o Actualización Semanal. Las metas se mantienen en sus departamentos.' : 'Este equipo aún no tiene objetivos principales.'}</div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderConfiguration = () => {
        const weeks = Array.isArray(selectedCycle?.weeks) ? selectedCycle.weeks : [];

        if (!selectedCycleId && !isLoadingCycles) {
            return (
                <EmptyState
                    title="Crea tu primer ciclo OKR para comenzar."
                    description="El calendario semanal del ciclo aparecerá aquí."
                    action={canCreateCycle ? <Button label="Nuevo Ciclo" icon="pi pi-plus" onClick={openCreateCycleDialog} className="mt-3" /> : null}
                />
            );
        }

        return (
            <div className="flex flex-column gap-3">
                <div className="surface-card border-round p-3 shadow-1">
                    <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3 mb-3">
                        <div>
                            <h3 className="m-0">Ciclo</h3>
                            <p className="text-color-secondary mt-2 mb-0">Administra la configuración general del ciclo OKR seleccionado.</p>
                            <div className="grid mt-2">
                                <div className="col-12 md:col-6 lg:col-3 text-sm">
                                    <span className="text-color-secondary">Nombre:</span> {selectedCycle?.name || '-'}
                                </div>
                                <div className="col-12 md:col-6 lg:col-3 text-sm">
                                    <span className="text-color-secondary">Inicio:</span> {formatDate(selectedCycle?.startDate)}
                                </div>
                                <div className="col-12 md:col-6 lg:col-3 text-sm">
                                    <span className="text-color-secondary">Cierre:</span> {formatDate(selectedCycle?.endDate)}
                                </div>
                                <div className="col-12 md:col-6 lg:col-3 text-sm">
                                    <span className="text-color-secondary">Semanas:</span> {formatNumber(selectedCycle?.numberOfWeeks)}
                                </div>
                                <div className="col-12 md:col-6 lg:col-3 text-sm">
                                    <span className="text-color-secondary">Puntos base:</span> {formatNumber(selectedCycle?.pointsBase)}
                                </div>
                                <div className="col-12 md:col-6 lg:col-3 text-sm">
                                    <span className="text-color-secondary mr-2">Estado:</span>
                                    {selectedCycle?.status ? <Tag value={getStatusLabel(selectedCycle.status)} severity={statusSeverity(selectedCycle.status)} /> : null}
                                </div>
                            </div>
                        </div>
                        {canCreateCycle ? (
                            <div className="flex flex-wrap gap-2">
                                <Button label="Nuevo ciclo" icon="pi pi-plus" className="p-button-outlined" onClick={openCreateCycleDialog} />
                                <Button label="Copiar a nuevo trimestre" icon="pi pi-copy" className="p-button-outlined" disabled={!selectedCycleId || (selectedCycle?.copyPreparation && selectedCycle.copyPreparation.state !== 'ready')}
                                    onClick={() => { setCopyPreview(null); setCopyError(''); setCopyDialogVisible(true); }} />
                                <Button label="Editar ciclo" icon="pi pi-pencil" className="p-button-outlined" onClick={openEditCycleDialog} disabled={!selectedCycleId} />
                                {selectedCycle?.status === 'draft' ? (
                                    <Button
                                        label="Activar ciclo"
                                        icon="pi pi-play"
                                        className="p-button-outlined p-button-success"
                                        onClick={handleActivateCycle}
                                        loading={isSavingCycleControl}
                                    />
                                ) : null}
                                {selectedCycle?.status === 'active' ? (
                                    <Button
                                        label="Cerrar ciclo"
                                        icon="pi pi-lock"
                                        className="p-button-outlined p-button-danger"
                                        onClick={handleCloseCycle}
                                        loading={isSavingCycleControl}
                                    />
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                </div>

                <div className="surface-card border-round p-3 shadow-1">
                    <div className="mb-3">
                        <h3 className="m-0">Semanas del ciclo</h3>
                        <p className="text-color-secondary mt-2 mb-0">Bloquea o desbloquea semanas desde la configuración del ciclo.</p>
                    </div>
                    <DataTable value={weeks} responsiveLayout="scroll" emptyMessage="Este ciclo no tiene semanas configuradas">
                        <Column field="weekNumber" header="Semana" sortable style={{ width: '8rem' }} />
                        <Column header="Fecha inicio" body={(rowData) => formatDate(rowData.startDate)} sortable style={{ minWidth: '150px' }} />
                        <Column header="Fecha cierre" body={(rowData) => formatDate(rowData.endDate)} sortable style={{ minWidth: '150px' }} />
                        <Column field="label" header="Label" sortable style={{ minWidth: '160px' }} />
                        <Column header="Estado" body={weekLockedBody} style={{ minWidth: '130px' }} />
                        {canCreateCycle ? <Column header="Acciones" body={weekActionsBody} style={{ minWidth: '190px' }} /> : null}
                    </DataTable>
                </div>

                {renderTeamWorkspace()}
                {renderExportSection()}
            </div>
        );
    };

    const renderDashboard = () => {
        if (!selectedCycleId && !isLoadingCycles) {
            return (
                <EmptyState
                    title="Crea tu primer ciclo OKR para comenzar."
                    description="Cuando exista un ciclo, aquí verás ranking, puntos y avance del trimestre."
                    action={canCreateCycle ? <Button label="Nuevo Ciclo" icon="pi pi-plus" onClick={openCreateCycleDialog} className="mt-3" /> : null}
                />
            );
        }

        return (
            <OkrExecutiveDashboard
                selectedCycle={selectedCycle}
                dashboard={dashboard}
                scoreboard={scoreboard}
                teams={teams}
                objectives={objectives}
                keyResults={keyResults}
                users={users}
                weeklyEntries={reportWeeklyEntries}
                currentWeek={currentWeek}
                canFilterByTeam={canFilterDashboardByTeam}
                loading={isLoadingSummary || isLoadingTeams || isLoadingObjectives || isLoadingKeyResults || isLoadingReportWeeklyEntries}
            />
        );
    };

    return (
        <div className="p-4 okr-module">
            <div className="flex flex-column lg:flex-row lg:align-items-start lg:justify-content-between gap-3 mb-4">
                <div>
                    <div className="flex align-items-center gap-2 mb-2">
                        <h2 className="m-0">OKR</h2>
                        {currentWeek?.weekNumber ? <Tag value={`Semana ${currentWeek.weekNumber}`} severity="info" /> : null}
                    </div>
                    <p className="text-color-secondary mt-0 mb-0">Seguimiento trimestral de objetivos, resultados clave y puntos por equipo.</p>
                </div>
                <div className="flex flex-column sm:flex-row gap-2 sm:align-items-center">
                    <Dropdown
                        value={selectedCycleId}
                        inputId="okr-cycle-selector"
                        aria-label="Ciclo OKR"
                        options={cycleOptions}
                        onChange={(event) => setSelectedCycleId(event.value)}
                        placeholder="Selecciona un ciclo"
                        loading={isLoadingCycles}
                        className="w-full sm:w-18rem"
                        disabled={!cycleOptions.length}
                    />
                </div>
            </div>

            {errorMessage ? (
                <div className="surface-card border-round p-3 mb-3 border-left-3 border-red-500">
                    <div className="flex align-items-center gap-2 text-red-600 font-semibold">
                        <i className="pi pi-exclamation-circle" />
                        <span>{errorMessage}</span>
                    </div>
                </div>
            ) : null}

            {selectedCycle ? <div className="surface-card border-round p-3">
                <strong>{selectedCycle.name}</strong> · {formatDate(selectedCycle.startDate)} a {formatDate(selectedCycle.endDate)} · {selectedCycle.numberOfWeeks} semanas
                {selectedCycle.calendarMode === CALENDAR_MODE ? <p className="mb-0">Calendario de Panamá: lunes a domingo, limitado al trimestre. Las semanas parciales conservan las metas semanales; pueden ajustarse en Configuración.</p> : null}
                {selectedCycle.copyPreparation?.sourceCycleId ? <p className="mb-0">Estructura copiada del ciclo anterior. Los avances y aprobaciones comienzan de nuevo.</p> : null}
                {selectedCycle.copyPreparation?.calendarAdjustment?.notice ? <p className="mb-0 text-orange-700" role="status">{selectedCycle.copyPreparation.calendarAdjustment.notice}</p> : null}
                {new Date(selectedCycle.endDate) < new Date() && selectedCycle.status !== 'closed' ? <p className="mb-0">Período finalizado; permanece disponible para revisar actualizaciones pendientes antes de cerrarlo.</p> : null}
            </div> : null}

            <TabView className="mt-4 okr-tabs">
                {[
                canCreateCycle ? <TabPanel key="dashboard" header="Dashboard">{renderDashboard()}</TabPanel> : null,
                <TabPanel key="my-okrs" header="Mis OKRs">
                    <OkrMyOkrs
                        selectedCycle={selectedCycle}
                        currentUser={currentUser}
                        teams={teams}
                        objectives={objectives}
                        keyResults={keyResults}
                        weeklyEntries={reportWeeklyEntries}
                        users={users}
                        scoreboard={scoreboard}
                        currentWeek={currentWeek}
                        selectedWeekNumber={selectedWeekNumber}
                        onWeekChange={setSelectedWeekNumber}
                        loading={isLoadingTeams || isLoadingObjectives || isLoadingKeyResults || isLoadingReportWeeklyEntries}
                        canManage={canCreateCycle}
                        onOpenEntry={openWeeklyEntryDialog}
                    />
                </TabPanel>,
                <TabPanel key="kpi-history" header="Indicadores semanales">
                    <OkrKpiHistory selectedCycle={selectedCycle} teams={teams} keyResults={keyResults} weeklyEntries={reportWeeklyEntries} users={users} canViewFinancial={canCreateCycle} loading={isLoadingTeams || isLoadingKeyResults || isLoadingReportWeeklyEntries} />
                </TabPanel>,
                canCreateCycle ? (
                    <TabPanel key="configuration" header="Configuración">
                        {renderConfiguration()}
                    </TabPanel>
                ) : null,
                canViewWeeklyReview ? (
                    <TabPanel key="weekly-review" header="Actualización Semanal">
                        <OkrWeeklyUpdates
                            selectedCycle={selectedCycle}
                            currentWeek={currentWeek}
                            selectedWeekNumber={selectedWeekNumber}
                            onWeekChange={setSelectedWeekNumber}
                            teams={teams}
                            objectives={objectives}
                            keyResults={keyResults}
                            weeklyEntries={weeklyEntries}
                            boardWeeklyEntries={reportWeeklyEntries}
                            users={users}
                            currentUser={currentUser}
                            canManage={canManageWeeklyEntries}
                            loading={isLoadingWeeklyEntries || isLoadingKeyResults}
                            scoreboard={scoreboard}
                            reviewingEntryId={reviewingEntryId}
                            onOpenEntry={openWeeklyEntryDialog}
                            onApproveEntry={handleApproveWeeklyEntry}
                            onRejectEntry={handleRejectWeeklyEntry}
                        />
                    </TabPanel>
                ) : null,
                ].filter(Boolean)}
            </TabView>

            <OkrCycleDialog
                visible={cycleDialogVisible}
                cycle={cycleDialogMode === 'edit' ? selectedCycle : null}
                saving={isSavingCycle}
                onHide={closeCycleDialog}
                onSubmit={handleSaveCycle}
            />
            <OkrCycleCopyDialog visible={copyDialogVisible} source={selectedCycle} busy={copyBusy} preview={copyPreview} error={copyError}
                onPreview={handlePreviewCopy} onCopy={handleCopyCycle} onHide={() => { if (!copyBusy) setCopyDialogVisible(false); }} />

            <OkrTeamDialog
                visible={teamDialogVisible}
                team={selectedTeam}
                users={users}
                saving={isSavingTeam}
                onHide={closeTeamDialog}
                onSubmit={handleSaveTeam}
            />

            <OkrObjectiveDialog
                visible={objectiveDialogVisible}
                objective={selectedObjective}
                teams={teams}
                users={users}
                defaultTotalWeight={Number(selectedCycle?.pointsBase || 1000)}
                initialTeamId={objectiveTeamId}
                saving={isSavingObjective}
                onHide={closeObjectiveDialog}
                onSubmit={handleSaveObjective}
            />

            <OkrKeyResultDialog
                visible={keyResultDialogVisible}
                keyResult={selectedKeyResult}
                team={keyResultContext.team}
                objective={keyResultContext.objective}
                cycle={selectedCycle}
                users={users}
                keyResults={keyResults}
                saving={isSavingKeyResult}
                onHide={closeKeyResultDialog}
                onSubmit={handleSaveKeyResult}
            />

            <OkrWeeklyEntryDialog
                visible={weeklyEntryDialogVisible}
                keyResult={weeklyEntryContext.keyResult}
                team={weeklyEntryContext.team}
                objective={weeklyEntryContext.objective}
                cycle={selectedCycle}
                weekNumber={weeklyEntryContext.weekNumber || selectedWeekNumber}
                entry={weeklyEntryContext.entry}
                saving={isSavingWeeklyEntry}
                locked={Boolean(selectedWeek?.isLocked || weeklyEntryContext.locked)}
                canOverrideLocked={canManageWeeklyEntries}
                onHide={closeWeeklyEntryDialog}
                onSubmit={handleSaveWeeklyEntry}
                onSyncFromSource={handleSyncWeeklyEntryFromSource}
            />
        </div>
    );
};

export default OKRModule;
